import { createPortal } from 'react-dom';
import { DocumentationPageVersionSummary } from '../../services/documentationApi';

type DocumentationVersionsModalProps = {
  open: boolean;
  pageTitle: string;
  versions: DocumentationPageVersionSummary[];
  loading: boolean;
  canWrite: boolean;
  onClose: () => void;
  onRestore: (versionId: string) => void;
};

function formatDate(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

function kindLabel(kind: string, isWorking: boolean): string {
  if (isWorking) return 'Em edição';
  if (kind === 'Published') return 'Publicada';
  return 'Rascunho';
}

export function DocumentationVersionsModal({
  open,
  pageTitle,
  versions,
  loading,
  canWrite,
  onClose,
  onRestore,
}: DocumentationVersionsModalProps) {
  if (!open) return null;

  return createPortal(
    <div className="docs-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="docs-modal docs-modal--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="docs-versions-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="docs-versions-title" className="docs-modal__title">
          Versões — {pageTitle}
        </h3>

        {loading ? (
          <p className="text-sm text-secondary-500">Carregando versões…</p>
        ) : versions.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhuma versão registrada.</p>
        ) : (
          <ul className="docs-versions-list">
            {versions.map((version) => {
              const canRestore =
                canWrite && version.kind === 'Published' && !version.isCurrentPublished;
              return (
                <li key={version.id} className="docs-versions-list__item">
                  <div className="docs-versions-list__main">
                    <div className="docs-versions-list__label-row">
                      <span className="docs-versions-list__label">{version.label}</span>
                      {version.isCurrentPublished ? (
                        <span className="docs-versions-list__badge">Atual</span>
                      ) : null}
                      {version.isWorking ? (
                        <span className="docs-versions-list__badge docs-versions-list__badge--draft">
                          Em edição
                        </span>
                      ) : null}
                    </div>
                    <div className="docs-versions-list__meta">
                      <span>{kindLabel(version.kind, version.isWorking)}</span>
                      <span>{version.title}</span>
                      <span>{formatDate(version.publishedAtUtc ?? version.createdAtUtc)}</span>
                    </div>
                  </div>
                  {canRestore ? (
                    <button
                      type="button"
                      className="erp-btn-secondary"
                      onClick={() => onRestore(version.id)}
                    >
                      Tornar atual
                    </button>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}

        <div className="mt-4 flex justify-end">
          <button type="button" className="erp-btn-secondary" onClick={onClose}>
            Fechar
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
